import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import {
  Users as UsersIcon, CalendarCheck, ClipboardText, Warning, BookOpen, ChartLine,
} from "@phosphor-icons/react";

const stat = (label, value, icon, tone = "text-indigo-700", testId) => ({
  label, value, icon, tone, testId,
});

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
  }, []);

  const items = data ? [
    stat("Students enrolled", data.students_total, UsersIcon, "text-indigo-700", "stat-students"),
    stat("Teaching & staff", data.staff_total, UsersIcon, "text-sky-700", "stat-staff"),
    stat("Pending tasks", data.tasks_pending, ClipboardText, "text-amber-700", "stat-tasks-pending"),
    stat("Overdue tasks", data.tasks_overdue, Warning, "text-rose-700", "stat-tasks-overdue"),
    stat("Lesson plans today", data.lesson_plans_today, BookOpen, "text-emerald-700", "stat-plans-today"),
    stat("Plans awaiting review", data.lesson_plans_pending_review, BookOpen, "text-indigo-700", "stat-plans-review"),
    stat("Overdue lesson plans", data.lesson_plans_overdue, Warning, "text-rose-700", "stat-plans-overdue"),
    stat("Exams logged today", data.exams_recorded_today, ChartLine, "text-emerald-700", "stat-exams-today"),
  ] : [];

  const att = data?.attendance_today || {};

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">
          Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, {user.name.split(" ")[0]}
        </h1>
        <p className="text-zinc-500 mt-1">
          Here&apos;s what&apos;s happening across the school today.
        </p>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <motion.div
              key={it.label}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.3 }}>
              <Card className="p-5 border-zinc-200" data-testid={it.testId}>
                <div className="flex items-start justify-between">
                  <div className="text-xs text-zinc-500 uppercase tracking-wide">{it.label}</div>
                  <Icon size={18} className={it.tone} weight="duotone" />
                </div>
                <div className="font-display text-3xl font-bold mt-3">{it.value}</div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Attendance snapshot */}
      <Card className="p-6 border-zinc-200">
        <div className="flex items-center gap-2 mb-4">
          <CalendarCheck size={18} className="text-indigo-700" weight="bold" />
          <h2 className="font-display text-lg font-bold">Student attendance — today</h2>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[
            ["Present", att.present || 0, "text-emerald-700", "bg-emerald-50"],
            ["Absent", att.absent || 0, "text-rose-700", "bg-rose-50"],
            ["Late", att.late || 0, "text-amber-700", "bg-amber-50"],
            ["Leave", att.leave || 0, "text-sky-700", "bg-sky-50"],
          ].map(([l, v, t, b]) => (
            <div key={l} className={`${b} rounded-md p-4`}>
              <div className={`text-xs uppercase tracking-wide ${t} font-semibold`}>{l}</div>
              <div className="font-display text-2xl font-bold mt-1">{v}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500 mt-4">
          Attendance is recorded class-wise under the Attendance page.
        </p>
      </Card>
    </div>
  );
}
